import React from 'react'
import { SearchX } from 'lucide-react'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface NoResultsStateProps {
  search: string
  status: string
  onClearFilters: () => void
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * NoResultsState - Displayed when filters match no clients
 *
 * Features:
 * - SearchX icon to indicate no results
 * - Shows active filter values as chips
 * - Suggests clearing filters
 * - Clear filters action button
 * - Monumenta design system styling
 *
 * @param search - Current search term
 * @param status - Current status filter
 * @param onClearFilters - Callback to clear all filters
 */
export function NoResultsState({ search, status, onClearFilters }: NoResultsStateProps) {
  const hasSearch = search !== ''
  const hasStatus = status !== 'all'

  return (
    <div className="card flex flex-col items-center justify-center min-h-[400px] space-y-4 animate-fade-in">
      {/* Icon */}
      <SearchX className="h-16 w-16 text-brand-gray opacity-50" />

      {/* Title */}
      <h3 className="text-xl font-display font-semibold text-white">
        No Clients Found
      </h3>

      {/* Description */}
      <div className="text-center text-brand-gray-light max-w-md space-y-2 font-body">
        <p>No clients match your current filters:</p>

        {/* Active Filters Display */}
        <div className="flex flex-wrap gap-2 justify-center mt-3 mb-3">
          {hasSearch && (
            <span className="px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary border border-brand-primary/20 text-sm">
              Search: "{search}"
            </span>
          )}
          {hasStatus && (
            <span className="px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary border border-brand-primary/20 text-sm">
              Status: {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          )}
        </div>

        <p className="mt-4">Try adjusting your filters or clearing them to see more results.</p>
      </div>

      {/* Clear Filters Button */}
      <button onClick={onClearFilters} className="btn-primary mt-2">
        Clear Filters
      </button>
    </div>
  )
}

export default NoResultsState
